import './Tutorial.css';
import Game from './Game.js';
import { useState, useEffect, useCallback } from 'react';

const SEEN_KEY = 'tutorialSeen';
export const TUTORIAL_EVENT = 'totalten:tutorial';

/**
 * Whether to teach on this launch. Anyone with a board already in their history
 * has worked it out, so only genuinely new players get it unasked.
 * @returns {boolean}
 */
export function shouldTeach(hasHistory) {
  try {
    return !hasHistory && localStorage.getItem(SEEN_KEY) !== 'true';
  } catch (error) {
    console.error('Error reading tutorial flag from localStorage:', error);
    return false;
  }
}

export function markTaught() {
  try {
    localStorage.setItem(SEEN_KEY, 'true');
  } catch (error) {
    console.error('Error writing tutorial flag to localStorage:', error);
  }
}

/**
 * A fixed throwaway board, never a generated one. Pointing at a real move on a
 * real game would just be a free hint, so the lesson gets its own grid.
 *
 * Four moves, disjoint and tiling the whole board, deliberately in increasing
 * size - so always pointing at the smallest remaining move walks the player
 * through every shape the rules allow, in order:
 *
 *      7  6  4  9      1. a row pair          6+4
 *      1  1  2  1      2. a column pair       9+1
 *      1  2  3  3      3. a block             1+1+2+1+2+3
 *                      4. the two corners     7+3
 *
 * The last one is the point of the whole thing: by then everything between the
 * corners is cleared, so the only way to take them is to drag a rectangle
 * straight over the gap. Cleared squares are worth zero, which is the rule
 * nobody works out on their own.
 */
const TUTORIAL_NUMBERS = [
  [7, 6, 4, 9],
  [1, 1, 2, 1],
  [1, 2, 3, 3],
];

export function createTutorialGame() {
  return new Game(TUTORIAL_NUMBERS.map((row) => [...row]));
}

/**
 * Ask for the tutorial from anywhere - Board is listening.
 *
 * The seen flag is left alone on purpose. It only decides whether the lesson
 * runs *unasked* on a launch, and Board starts this one on the event regardless
 * - so clearing it would do nothing here except arm the unprompted tutorial
 * again for anyone who picks the lesson from the menu and then wanders off.
 */
export function replayTutorial() {
  window.dispatchEvent(new CustomEvent(TUTORIAL_EVENT));
}

/**
 * The move worth demonstrating: the smallest on the board, preferring a
 * horizontal pair because it is the easiest shape to read as "drag across".
 * @param {Object} game
 * @returns {{top: number, left: number, bottom: number, right: number} | null}
 */
export function pickTeachingMove(game) {
  const moves = game.getValidMoves(game.getNumbers());
  if (moves.length === 0) return null;

  const area = ({ top, left, bottom, right }) => (bottom - top + 1) * (right - left + 1);
  // Area dominates; a flat move breaks the tie
  const rank = (move) => area(move) * 2 + (move.top === move.bottom ? 0 : 1);
  return moves.reduce((best, move) => (rank(move) < rank(best) ? move : best));
}

/**
 * How far the ghost sits outside the squares it covers, so its edge reads as a
 * selection around them rather than a border drawn on them. Cells are 4px apart
 * on each side, so this stays inside the gutter and never touches a neighbour.
 */
const GHOST_PAD = 3;

/** Screen box covering the move, measured off the real cells */
function measure(move) {
  if (!move) return null;
  const first = document.getElementById(`cell-${move.top}-${move.left}`);
  const last = document.getElementById(`cell-${move.bottom}-${move.right}`);
  if (!first || !last) return null;

  const a = first.getBoundingClientRect();
  const b = last.getBoundingClientRect();
  // A cell with no box has not been laid out yet, and every coordinate off it
  // is zero - which parks the hand and the ghost in the corner of the screen.
  // Better no hand for a frame than a hand in the wrong place.
  if (a.width === 0 || a.height === 0) return null;
  return {
    left: a.left - GHOST_PAD,
    top: a.top - GHOST_PAD,
    width: b.right - a.left + GHOST_PAD * 2,
    height: b.bottom - a.top + GHOST_PAD * 2,
  };
}

/**
 * Whether a selection is the move being taught. The lesson only accepts the
 * move it is pointing at - the board offers other valid tens, and a player who
 * takes one lands on a prompt describing a shape they never dragged.
 * @param {Object} move
 * @param {{i: number, j: number}[]} indices - cells under the drag
 * @returns {boolean}
 */
export function coversMove(move, indices) {
  if (!move || indices.length === 0) return false;
  const rows = indices.map(({ i }) => i);
  const cols = indices.map(({ j }) => j);
  // A drag is always a rectangle, so its bounds are the whole of it
  return Math.min(...rows) === move.top && Math.max(...rows) === move.bottom
    && Math.min(...cols) === move.left && Math.max(...cols) === move.right;
}

const TUTORIAL_TOTAL = TUTORIAL_NUMBERS.flat().reduce((a, b) => a + b, 0);
/** Every move takes exactly ten off the board, so the count is the arithmetic */
export const TUTORIAL_MOVES = TUTORIAL_TOTAL / 10;
const stepNumber = (numbers) =>
  (TUTORIAL_TOTAL - numbers.flat().reduce((a, b) => a + b, 0)) / 10 + 1;

/**
 * Describes the shape actually being pointed at rather than counting steps, so
 * the words still match if the board is played out of order.
 * @param {Object} move
 * @param {number[][]} numbers
 * @returns {string}
 */
function promptFor(move, numbers) {
  if (!move) return 'Press and drag across the lit tiles.';

  let spansGap = false;
  for (let row = move.top; row <= move.bottom; row++) {
    for (let col = move.left; col <= move.right; col++) {
      if (numbers[row][col] === 0) spansGap = true;
    }
  }

  // The one nobody works out on their own, so it wins over the shape wording
  if (spansGap) return 'Tiles do not need to be adjacent. Drag over the gap.';
  if (move.top !== move.bottom && move.left !== move.right) return 'Any rectangle counts, not just lines.';
  if (move.left === move.right) return 'Columns work the same way. Drag down.';
  return 'Drag across the two lit tiles that sum to 10.';
}

/** The sum spelled out, so the goal is on screen and not just in the prompt */
function sumLine(move, numbers) {
  if (!move) return null;
  const values = [];
  for (let row = move.top; row <= move.bottom; row++) {
    for (let col = move.left; col <= move.right; col++) {
      if (numbers[row][col] !== 0) values.push(numbers[row][col]);
    }
  }
  if (values.length === 0) return null;
  return values.length <= 3 ? `${values.join(' + ')} = 10` : `${values.length} tiles, 10 in total`;
}

// Answers the mistake that was actually made, rather than restating the prompt
const NUDGES = {
  tap: 'One tap is not enough — press, then drag across both tiles.',
  off: 'Not those — take the lit tiles first.',
};

/**
 * The hand doing the gesture. A dot travelling across the board reads as an
 * animation playing; a hand reads as someone's hand, which is the whole point -
 * the next one has to be theirs.
 *
 * The pointing fingertip is at (19, 10) in this 48x54 box, and that - not the
 * middle of the hand - is what CSS parks on the cell. Decorative: the banner
 * underneath is the version a screen reader should read.
 *
 * Exported because demo builds put the same hand on a real drag, so that a
 * screenshot of ordinary play shows the gesture - see GlobalClickListener.
 */
export function GhostHand({ className = 'tutorial-ghost-hand', style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 48 54" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16.5 27 V10 a2.5 2.5 0 0 1 5 0 V21 a2.5 2.5 0 0 1 5 0 V22
                 a2.5 2.5 0 0 1 5 0 V23 a2.5 2.5 0 0 1 5 0 V30
                 a10 10 0 0 1 -10 10 h-5 a10 10 0 0 1 -10 -10
                 V27 a2.5 2.5 0 0 1 5 0 Z" />
      </g>
    </svg>
  );
}

function Tutorial({ step, move, numbers, nudge = null }) {
  const [box, setBox] = useState(null);

  const remeasure = useCallback(() => setBox(measure(move)), [move]);

  useEffect(() => {
    if (step !== 'playing') return undefined;
    // Cells animate in and shrink out, so let the board settle before measuring
    const settle = setTimeout(remeasure, 160);
    window.addEventListener('resize', remeasure);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('resize', remeasure);
    };
  }, [step, remeasure]);

  // No timer on the closing message - it is the payoff, and the board is
  // finished, so it stays until they pick New Game or Try Again
  if (step === 'cleared') {
    return (
      <div className="tutorial-banner tutorial-banner-done">
        <span>That is the whole game.</span>
        <small>Every board can be cleared completely.</small>
      </div>
    );
  }

  if (step !== 'playing') return null;

  return (
    <>
      {box && (
        <div
          className="tutorial-ghost"
          style={{
            left: box.left,
            top: box.top,
            '--w': `${box.width}px`,
            '--h': `${box.height}px`,
          }}
        >
          <span className="tutorial-ghost-box" />
          <span className="tutorial-ghost-press" />
          <GhostHand />
        </div>
      )}
      {/* There is no Skip: four moves is short enough to finish, and the people
          this exists for are the ones who would have skipped it */}
      <div className="tutorial-banner">
        <span className="tutorial-step">Step {stepNumber(numbers)} of {TUTORIAL_MOVES}</span>
        {nudge
          ? <span className="tutorial-nudge">{NUDGES[nudge]}</span>
          : <span>{promptFor(move, numbers)}</span>}
        <small>{sumLine(move, numbers)}</small>
      </div>
    </>
  );
}

export default Tutorial;
